import React, { useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import TicketsPreparacion from "./TicketsPreparacion";
import Toasts from "../../../../components/notificacion/Toasts";
import { StateContext } from "../../../../components/context/StateProvider";
import { getErrorMessage } from "../../../../utils/ErrorUtils";
import AuthService from "../../../../services/auth.service";

/**
 * Botón de listado que genera e imprime una etiqueta de preparación de Nave 2 (de un
 * grupo o de un pedido suelto). Lo envuelven BotonEtiquetaGrupoN2 y BotonEtiquetaPedidoN2.
 *
 * Si hay productos en altura o Tronja 3 imprime también su etiqueta (TicketsPreparacion).
 *
 * @param {Object} props
 * @param {Function} props.cargarTicket - Recibe el ejercicio y devuelve `{ order, products }`
 * @param {string} props.mensajeVacio - Aviso si no queda ningún producto que imprimir
 * @param {string} props.mensajeError - Aviso por defecto si falla la carga
 * @param {Object|null} [props.preparacion] - Si llega, se comporta como el "Imprimir" del
 *   punteo: `asignarPreparador(preparador, year)` antes de imprimir (el ticket ya sale con
 *   él) y `pasarAEnPreparacion(year)` después. Sin ella solo imprime.
 *
 * DECISION: el preparador es el usuario conectado (decisión del usuario): quien imprime
 * la etiqueta desde el listado es quien va a preparar.
 */
const BotonEtiquetaN2 = ({ cargarTicket, mensajeVacio, mensajeError, preparacion = null }) => {
    const { state, updateState } = useContext(StateContext);

    // El toast se acumula sobre los actuales, que pueden haber cambiado mientras se cargaba
    const estadoRef = useRef(state);
    estadoRef.current = state;

    const [ticket, setTicket] = useState(null);
    const [generando, setGenerando] = useState(false);
    const ticketRef = useRef(null);
    // Ejercicio de la impresión en curso que debe pasar a "en preparación" al terminar.
    // Ref y no estado: onAfterPrint es el de la llamada a imprimir, de un render anterior
    const pendienteRef = useRef(null);

    const imprimir = useReactToPrint({
        content: () => ticketRef.current,
        onAfterPrint: () => {
            setTicket(null);
            const pendiente = pendienteRef.current;
            pendienteRef.current = null;
            if (pendiente) iniciarPreparacion(pendiente);
        }
    });

    // El ticket tiene que estar pintado antes de imprimir: se lanza al aplicarse
    useEffect(() => {
        if (ticket) imprimir();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket]);

    const notificar = (mensaje, tipo) => {
        const toast = Toasts.generateToast(mensaje, tipo);
        updateState({ toasts: [...estadoRef.current.toasts, toast] });
    };

    const iniciarPreparacion = async ({ year }) => {
        try {
            await preparacion.pasarAEnPreparacion(year);
            notificar('Pasado a en preparación', 'success');
        } catch (error) {
            console.error('Error al pasar a en preparación:', error);
            notificar(getErrorMessage(error, 'No se pudo pasar a en preparación'), 'error');
        }
    };

    const generar = async (e) => {
        // La fila entera abre el punteo del grupo
        e.stopPropagation();

        setGenerando(true);
        try {
            const year = estadoRef.current.year;
            const datos = await cargarTicket(year);
            if (datos.products.length === 0) {
                notificar(mensajeVacio, 'warning');
                return;
            }
            const preparador = preparacion ? AuthService.getCurrentUser()?.username?.toLowerCase() : null;
            if (preparador) {
                await preparacion.asignarPreparador(preparador, year);
                pendienteRef.current = { year };
            }
            // Copia: el ticket sale con el preparador recién asignado sin tocar lo cargado
            setTicket(preparador ? { ...datos, order: { ...datos.order, preparer: preparador } } : datos);
        } catch (error) {
            console.error('Error al generar la etiqueta de nave 2:', error);
            notificar(getErrorMessage(error, mensajeError), 'error');
        } finally {
            setGenerando(false);
        }
    };

    const titulo = 'Imprimir etiqueta de preparación';

    return (
        <>
            <IconButton
                size="small"
                aria-label={titulo}
                title={titulo}
                onClick={generar}
                disabled={generando}
                sx={{
                    color: '#6c757d',
                    '&:hover': { color: '#cc071e' },
                    p: { xs: 0.5, sm: 0.75 }
                }}
            >
                {generando
                    ? <CircularProgress size={18} sx={{ color: '#cc071e' }} />
                    : <PrintIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            </IconButton>
            {ticket && (
                <TicketsPreparacion ref={ticketRef} order={ticket.order} products={ticket.products} />
            )}
        </>
    );
};

export default BotonEtiquetaN2;
